import React, { useState, useEffect, useCallback, useMemo } from 'react';
import QuoteGrid from './QuoteGrid';
import QuoteModal from './QuoteModal';
import QuotesPreview from './QuotesPreview';
import DeleteConfirmModal from '../../common/DeleteConfirmModal';
import ViewToolbar from '../../common/ViewToolbar';
import { useModal } from '../../../hooks/useModal';
import { usePeriodFilter } from '../../../hooks/usePeriodFilter';
import { useDebounce } from '../../../hooks/useDebounce';
import * as quoteService from '../../../services/quoteService';

export default function QuotesView({ onImportToInvoice }) {
  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [setup, setSetup] = useState([]);
  const [setup2, setSetup2] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm);
  const [gridApi, setGridApi] = useState(null);

  const [subView, setSubView] = useState('grid');
  const [draftQuote, setDraftQuote] = useState(null);
  const [draftToRestore, setDraftToRestore] = useState(null);

  const editModal = useModal();
  const deleteModal = useModal();
  const periodFilter = usePeriodFilter();

  const filteredQuotes = useMemo(
    () => periodFilter.filterByPeriod(quotes, 'quotation_date'),
    [quotes, periodFilter],
  );

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await quoteService.fetchQuotes(debouncedSearchTerm);
      setQuotes(result.quotes);
      setCustomers(result.customers);
      setItems(result.items);
      setTaxes(result.taxes);
      setSetup(result.setup);
      setSetup2(result.setup2);
    } catch (err) {
      console.error('Error fetching quotes:', err);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async (formData) => {
    try {
      await quoteService.saveQuote(formData, editModal.record?.quotation_id || null);
      editModal.close();
      fetchData();
    } catch (err) {
      alert('Failed to save: ' + err.message);
    }
  };

  const handleDelete = async () => {
    try {
      await quoteService.deleteQuote(deleteModal.record.quotation_id);
      deleteModal.close();
      fetchData();
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  const handlePreview = (draft) => {
    setDraftQuote(draft);
    setSubView('preview');
    editModal.close();
  };

  const handleBackFromPreview = (draft) => {
    setSubView('grid');
    setDraftToRestore(draft);
    const record = draft?.formData?.quotation_id != null
      ? quotes.find((q) => q.quotation_id === draft.formData.quotation_id) ?? null
      : null;
    editModal.open(record);
  };

  if (subView === 'preview') {
    return (
      <QuotesPreview
        draftQuote={draftQuote}
        onBack={() => handleBackFromPreview(draftQuote)}
      />
    );
  }

  return (
    <>
      <ViewToolbar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onAddNew={() => editModal.open(null)}
        onExportCsv={() => gridApi?.exportToCsv()}
        placeholder="Search quotes..."
        addLabel="New Quote"
        addColor="bg-emerald-500 hover:bg-emerald-600"
        period={periodFilter.period}
        onPeriodChange={periodFilter.onPeriodChange}
        dateFrom={periodFilter.dateFrom}
        onDateFromChange={periodFilter.onDateFromChange}
        dateTo={periodFilter.dateTo}
        onDateToChange={periodFilter.onDateToChange}
      />

      <div className="flex-1 min-h-0">
        {loading ? (
          <div className="flex justify-center mt-10">Loading...</div>
        ) : (
          <QuoteGrid
            rowData={filteredQuotes}
            onEdit={(r) => editModal.open(r)}
            onDelete={(r) => deleteModal.open(r)}
            onGridReady={setGridApi}
          />
        )}
      </div>

      <QuoteModal
        isOpen={editModal.isOpen}
        onClose={() => { editModal.close(); setDraftToRestore(null); }}
        onSubmit={handleSave}
        initialData={editModal.record}
        draftToRestore={draftToRestore}
        onClearDraftRestore={() => setDraftToRestore(null)}
        customers={customers}
        items={items}
        taxes={taxes}
        setup={setup}
        setup2={setup2}
        onPreview={handlePreview}
        quotes={quotes}
        onImportToInvoice={onImportToInvoice}
      />

      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.close}
        onConfirm={handleDelete}
        recordName={
          deleteModal.record?.quotation_number
            ? `Quote #${deleteModal.record.quotation_number}`
            : 'this quote'
        }
      />
    </>
  );
}
